/**
 * Conversion Engine — Global JavaScript
 * Vanilla JS. Zero frameworks. Zero jQuery.
 * Handles: drawers, cart AJAX, mega menu, predictive search,
 *          mobile nav, sticky header, toasts, accordions, tracking.
 */

const CE = window.CE || {};
window.CE = CE;

/* ============================================================
   Utility Helpers
   ============================================================ */

CE.debounce = (fn, delay = 300) => {
  let timer;
  return (...args) => {
    clearTimeout(timer);
    timer = setTimeout(() => fn(...args), delay);
  };
};

CE.formatMoney = (cents, format) => {
  if (typeof cents === 'string') cents = cents.replace('.', '');
  const moneyFormat = format || window.CE.moneyFormat || '£{{amount}}';
  const value = (cents / 100).toFixed(2);
  return moneyFormat
    .replace('{{amount}}', value)
    .replace('{{amount_no_decimals}}', Math.round(cents / 100))
    .replace('{{amount_with_comma_separator}}', value.replace('.', ','));
};

CE.fetchJSON = async (url, options = {}) => {
  const defaults = {
    headers: {
      'Content-Type': 'application/json',
      'Accept': 'application/json',
    },
  };
  const response = await fetch(url, { ...defaults, ...options });
  if (!response.ok) throw new Error(`HTTP ${response.status}`);
  return response.json();
};

/* ============================================================
   Drawer System
   ============================================================ */

CE.Drawer = {
  overlay: null,
  activeDrawer: null,
  returnFocus: null,

  init() {
    this.overlay = document.getElementById('DrawerOverlay');
    if (this.overlay) {
      this.overlay.addEventListener('click', () => this.closeAll());
    }
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && this.activeDrawer) {
        this.closeAll();
      }
    });
  },

  open(drawerId) {
    const drawer = document.getElementById(drawerId);
    if (!drawer) return;

    this.returnFocus = document.activeElement;
    this.activeDrawer = drawer;
    drawer.classList.add('is-active');
    if (this.overlay) this.overlay.classList.add('is-active');
    document.body.style.overflow = 'hidden';

    const focusable = drawer.querySelector('button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])');
    if (focusable) setTimeout(() => focusable.focus(), 100);

    document.dispatchEvent(new CustomEvent('drawer:open', { detail: { id: drawerId } }));
  },

  close(drawerId) {
    const drawer = document.getElementById(drawerId);
    if (!drawer) return;

    drawer.classList.remove('is-active');
    if (this.overlay) this.overlay.classList.remove('is-active');
    document.body.style.overflow = '';
    this.activeDrawer = null;

    if (this.returnFocus) {
      this.returnFocus.focus();
      this.returnFocus = null;
    }

    document.dispatchEvent(new CustomEvent('drawer:close', { detail: { id: drawerId } }));
  },

  closeAll() {
    document.querySelectorAll('.drawer.is-active').forEach(d => d.classList.remove('is-active'));
    if (this.overlay) this.overlay.classList.remove('is-active');
    document.body.style.overflow = '';
    this.activeDrawer = null;
    if (this.returnFocus) {
      this.returnFocus.focus();
      this.returnFocus = null;
    }
  },

  toggle(drawerId) {
    const drawer = document.getElementById(drawerId);
    if (!drawer) return;
    drawer.classList.contains('is-active') ? this.close(drawerId) : this.open(drawerId);
  }
};

/* ============================================================
   Sticky Header — hide on scroll down, show on scroll up
   ============================================================ */

CE.StickyHeader = {
  init() {
    const header = document.querySelector('.header');
    if (!header || !header.classList.contains('header--sticky')) return;

    let lastScroll = 0;
    let headerHeight = header.offsetHeight;
    const threshold = 5;

    document.documentElement.style.setProperty('--header-height', headerHeight + 'px');

    window.addEventListener('scroll', () => {
      const currentScroll = window.scrollY;

      // Always show at top
      if (currentScroll <= headerHeight) {
        header.classList.remove('header--hidden');
        header.classList.remove('header--scrolled');
        lastScroll = currentScroll;
        return;
      }

      header.classList.add('header--scrolled');

      // Ignore small scroll amounts
      if (Math.abs(currentScroll - lastScroll) < threshold) return;

      if (currentScroll > lastScroll && currentScroll > headerHeight * 2) {
        // Scrolling down
        header.classList.add('header--hidden');
      } else {
        // Scrolling up
        header.classList.remove('header--hidden');
      }

      lastScroll = currentScroll;
    }, { passive: true });
  }
};

/* ============================================================
   Mega Menu — hover on desktop, integrated with mobile drawer
   ============================================================ */

CE.MegaMenu = {
  init() {
    const navItems = document.querySelectorAll('.nav-list__item[data-mega-menu]');
    if (!navItems.length) return;

    navItems.forEach(item => {
      const megaMenu = item.querySelector('.mega-menu');
      const link = item.querySelector('.nav-list__link');
      if (!megaMenu) return;

      let closeTimeout;

      // Desktop: hover
      item.addEventListener('mouseenter', () => {
        clearTimeout(closeTimeout);
        // Close any other open menus
        document.querySelectorAll('.mega-menu.is-active').forEach(m => {
          if (m !== megaMenu) m.classList.remove('is-active');
        });
        megaMenu.classList.add('is-active');
        link.setAttribute('aria-expanded', 'true');
      });

      item.addEventListener('mouseleave', () => {
        closeTimeout = setTimeout(() => {
          megaMenu.classList.remove('is-active');
          link.setAttribute('aria-expanded', 'false');
        }, 150);
      });

      // Keyboard: Enter/Space toggles
      link.addEventListener('keydown', (e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          const isOpen = megaMenu.classList.contains('is-active');
          document.querySelectorAll('.mega-menu.is-active').forEach(m => m.classList.remove('is-active'));
          if (!isOpen) {
            megaMenu.classList.add('is-active');
            link.setAttribute('aria-expanded', 'true');
            const firstLink = megaMenu.querySelector('a');
            if (firstLink) firstLink.focus();
          } else {
            link.setAttribute('aria-expanded', 'false');
          }
        }
      });
    });

    // Close all megas when clicking outside
    document.addEventListener('click', (e) => {
      if (!e.target.closest('.nav-list__item[data-mega-menu]')) {
        document.querySelectorAll('.mega-menu.is-active').forEach(m => m.classList.remove('is-active'));
      }
    });

    // Close on Escape
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape') {
        document.querySelectorAll('.mega-menu.is-active').forEach(m => m.classList.remove('is-active'));
        document.querySelectorAll('[aria-expanded="true"]').forEach(el => el.setAttribute('aria-expanded', 'false'));
      }
    });
  }
};

/* ============================================================
   Mobile Navigation Drawer
   ============================================================ */

CE.MobileNav = {
  init() {
    const toggle = document.querySelector('[data-mobile-menu-toggle]');
    if (!toggle) return;

    toggle.addEventListener('click', (e) => {
      e.preventDefault();
      CE.Drawer.toggle('MobileNavDrawer');
    });

    // Sub-menu accordion in mobile nav
    document.querySelectorAll('.mobile-nav__link[data-has-submenu]').forEach(link => {
      link.addEventListener('click', (e) => {
        e.preventDefault();
        const item = link.closest('.mobile-nav__item');
        const submenu = item.querySelector('.mobile-nav__submenu');
        if (!submenu) return;

        const isOpen = link.getAttribute('aria-expanded') === 'true';
        link.setAttribute('aria-expanded', !isOpen);
        submenu.hidden = isOpen;

        // Close others
        link.closest('.mobile-nav__list').querySelectorAll('.mobile-nav__link[aria-expanded="true"]').forEach(other => {
          if (other !== link) {
            other.setAttribute('aria-expanded', 'false');
            const otherSub = other.closest('.mobile-nav__item').querySelector('.mobile-nav__submenu');
            if (otherSub) otherSub.hidden = true;
          }
        });
      });
    });
  }
};

/* ============================================================
   Search Overlay
   ============================================================ */

CE.Search = {
  overlay: null,
  input: null,
  results: null,
  isOpen: false,

  init() {
    this.overlay = document.getElementById('SearchOverlay');
    if (!this.overlay) return;

    this.input = this.overlay.querySelector('.search-overlay__input');
    this.results = this.overlay.querySelector('.search-overlay__results');

    // Toggle buttons
    document.querySelectorAll('[data-search-toggle]').forEach(btn => {
      btn.addEventListener('click', (e) => {
        e.preventDefault();
        this.toggle();
      });
    });

    // Close button
    this.overlay.querySelectorAll('[data-search-close]').forEach(btn => {
      btn.addEventListener('click', (e) => {
        e.preventDefault();
        this.close();
      });
    });

    // Input: predictive search
    if (this.input) {
      this.input.addEventListener('input', CE.debounce(() => {
        this.predict(this.input.value.trim());
      }, 300));

      // Close on Escape from input
      this.input.addEventListener('keydown', (e) => {
        if (e.key === 'Escape') this.close();
      });
    }
  },

  open() {
    if (!this.overlay) return;
    this.isOpen = true;
    this.overlay.classList.add('is-active');
    if (this.input) setTimeout(() => this.input.focus(), 100);
  },

  close() {
    if (!this.overlay) return;
    this.isOpen = false;
    this.overlay.classList.remove('is-active');
    if (this.input) this.input.value = '';
    if (this.results) this.results.innerHTML = '';
  },

  toggle() {
    this.isOpen ? this.close() : this.open();
  },

  async predict(query) {
    if (!this.results) return;

    if (query.length < 2) {
      this.results.innerHTML = '';
      return;
    }

    try {
      const response = await fetch(
        `/search/suggest.json?q=${encodeURIComponent(query)}&resources[type]=product,collection,page&resources[limit]=6`
      );
      const data = await response.json();
      const resources = data.resources?.results || {};

      let html = '';

      // Products (filter out phone-case-hidden siblings - keep only display product)
      const rawProducts = resources.products || [];
      const products = rawProducts.filter(function(p) {
        if (!p.url || !p.url.includes('tough-phone-case')) return true;
        return p.url.includes('iphone-16-pro-max');
      });
      if (products.length > 0) {
        html += '<div class="search-results__group">';
        html += '<h4 class="search-results__heading text-xs text-uppercase font-semibold">Products</h4>';
        products.forEach(product => {
          const img = product.image ? `<img src="${product.image}" alt="${product.title}" width="50" height="50" loading="lazy">` : '';
          html += `
            <a href="${product.url}" class="search-results__item">
              <div class="search-results__image">${img}</div>
              <div class="search-results__info">
                <span class="search-results__title text-sm">${product.title}</span>
                <span class="search-results__price text-sm">${product.price}</span>
              </div>
            </a>`;
        });
        html += '</div>';
      }

      // Collections
      const collections = resources.collections || [];
      if (collections.length > 0) {
        html += '<div class="search-results__group">';
        html += '<h4 class="search-results__heading text-xs text-uppercase font-semibold">Collections</h4>';
        collections.forEach(collection => {
          html += `<a href="${collection.url}" class="search-results__item search-results__item--text">
            <span class="text-sm">${collection.title}</span>
          </a>`;
        });
        html += '</div>';
      }

      // Pages
      const pages = resources.pages || [];
      if (pages.length > 0) {
        html += '<div class="search-results__group">';
        html += '<h4 class="search-results__heading text-xs text-uppercase font-semibold">Pages</h4>';
        pages.forEach(page => {
          html += `<a href="${page.url}" class="search-results__item search-results__item--text">
            <span class="text-sm">${page.title}</span>
          </a>`;
        });
        html += '</div>';
      }

      // View all link
      if (products.length > 0 || collections.length > 0 || pages.length > 0) {
        html += `<a href="/search?q=${encodeURIComponent(query)}" class="search-results__view-all btn btn--secondary btn--sm">
          View all results
        </a>`;
      } else {
        html += '<p class="search-results__empty text-sm">No results found</p>';
      }

      this.results.innerHTML = html;
    } catch (e) {
      console.error('Predictive search failed:', e);
    }
  }
};

/* ============================================================
   Cart API
   ============================================================ */

CE.Cart = {
  async get() {
    return CE.fetchJSON('/cart.js');
  },

  async add(items) {
    const data = await CE.fetchJSON('/cart/add.js', {
      method: 'POST',
      body: JSON.stringify({ items: Array.isArray(items) ? items : [items] }),
    });
    document.dispatchEvent(new CustomEvent('cart:updated'));
    document.dispatchEvent(new CustomEvent('cart:item-added', { detail: data }));
    CE.Track.event('add_to_cart', { items: data.items || [] });
    return data;
  },

  async update(updates) {
    const data = await CE.fetchJSON('/cart/update.js', {
      method: 'POST',
      body: JSON.stringify({ updates }),
    });
    document.dispatchEvent(new CustomEvent('cart:updated'));
    return data;
  },

  async change(line, quantity) {
    const data = await CE.fetchJSON('/cart/change.js', {
      method: 'POST',
      body: JSON.stringify({ line, quantity }),
    });
    document.dispatchEvent(new CustomEvent('cart:updated'));
    if (quantity === 0) CE.Track.event('remove_from_cart', { line });
    return data;
  },

  updateCount(count) {
    document.querySelectorAll('[data-cart-count]').forEach(el => {
      el.textContent = count;
      el.hidden = count === 0;
    });
  },

  async refreshDrawer() {
    try {
      const response = await fetch('/?sections=cart-drawer');
      const data = await response.json();
      const html = data['cart-drawer'];
      if (!html) return;

      const parser = new DOMParser();
      const doc = parser.parseFromString(html, 'text/html');
      const newDrawer = doc.querySelector('#CartDrawer');
      const currentDrawer = document.querySelector('#CartDrawer');

      if (newDrawer && currentDrawer) {
        // Replace body
        const newBody = newDrawer.querySelector('.drawer__body');
        const currentBody = currentDrawer.querySelector('.drawer__body');
        if (newBody && currentBody) currentBody.innerHTML = newBody.innerHTML;

        // Replace footer
        const newFooter = newDrawer.querySelector('.drawer__footer');
        const currentFooter = currentDrawer.querySelector('.drawer__footer');
        if (newFooter && currentFooter) {
          currentFooter.innerHTML = newFooter.innerHTML;
        } else if (newFooter && !currentFooter) {
          currentDrawer.appendChild(newFooter.cloneNode(true));
        } else if (!newFooter && currentFooter) {
          currentFooter.remove();
        }

        // Update header count
        const newCount = newDrawer.querySelector('.cart-drawer__count');
        const currentCount = currentDrawer.querySelector('.cart-drawer__count');
        if (newCount && currentCount) currentCount.innerHTML = newCount.innerHTML;
      }

      // Also update cart count in header
      const cart = await CE.Cart.get();
      CE.Cart.updateCount(cart.item_count);
    } catch (e) {
      console.error('Cart drawer refresh failed:', e);
    }
  }
};

/* ============================================================
   Toast Notifications
   ============================================================ */

CE.Toast = {
  el: null,
  timeout: null,

  show(message, duration = 3000) {
    this.el = this.el || document.getElementById('Toast');
    if (!this.el) return;
    clearTimeout(this.timeout);
    this.el.textContent = message;
    this.el.classList.add('is-active');
    this.timeout = setTimeout(() => this.el.classList.remove('is-active'), duration);
  }
};

/* ============================================================
   Event Tracking
   ============================================================ */

CE.Track = {
  event(name, data = {}) {
    if (window.dataLayer) window.dataLayer.push({ event: name, ...data });
    document.dispatchEvent(new CustomEvent(`ce:${name}`, { detail: data }));
  }
};

/* ============================================================
   Accordion
   ============================================================ */

CE.Accordion = {
  init() {
    document.querySelectorAll('.accordion__trigger').forEach(trigger => {
      trigger.addEventListener('click', () => {
        const expanded = trigger.getAttribute('aria-expanded') === 'true';
        trigger.setAttribute('aria-expanded', !expanded);
        const content = trigger.nextElementSibling;
        if (content) content.hidden = expanded;
      });
    });
  }
};

/* ============================================================
   Quantity Selector
   ============================================================ */

CE.QuantitySelector = {
  init() {
    document.addEventListener('click', (e) => {
      const btn = e.target.closest('.quantity-selector__btn');
      if (!btn) return;
      const wrapper = btn.closest('.quantity-selector');
      const input = wrapper.querySelector('.quantity-selector__input');
      if (!input) return;
      const current = parseInt(input.value, 10) || 1;
      const min = parseInt(input.min, 10) || 1;
      const max = parseInt(input.max, 10) || 999;
      if (btn.dataset.action === 'decrease' && current > min) input.value = current - 1;
      else if (btn.dataset.action === 'increase' && current < max) input.value = current + 1;
      input.dispatchEvent(new Event('change', { bubbles: true }));
    });
  }
};

/* ============================================================
   Product Gallery — thumbnail clicks + swipe
   ============================================================ */

CE.Gallery = {
  init() {
    const mainImg = document.getElementById('ProductMainImg');
    if (!mainImg) return;

    // Thumbnail clicks
    document.querySelectorAll('.product-main__thumb').forEach(thumb => {
      thumb.addEventListener('click', function() {
        document.querySelectorAll('.product-main__thumb').forEach(t => t.classList.remove('is-active'));
        this.classList.add('is-active');
        mainImg.src = this.dataset.imageUrl;
        if (this.dataset.imageSrcset) mainImg.srcset = this.dataset.imageSrcset;
        mainImg.alt = this.dataset.imageAlt || '';
      });
    });

    // Mobile swipe on main image container
    const container = document.getElementById('ProductMainImage');
    if (!container) return;

    let startX = 0;
    let diff = 0;
    const thumbs = [...document.querySelectorAll('.product-main__thumb')];
    if (thumbs.length < 2) return;

    container.addEventListener('touchstart', (e) => {
      startX = e.touches[0].clientX;
    }, { passive: true });

    container.addEventListener('touchend', (e) => {
      diff = startX - e.changedTouches[0].clientX;
      if (Math.abs(diff) < 50) return; // too small

      const current = thumbs.findIndex(t => t.classList.contains('is-active'));
      let next;
      if (diff > 0) {
        // Swipe left = next
        next = current < thumbs.length - 1 ? current + 1 : 0;
      } else {
        // Swipe right = prev
        next = current > 0 ? current - 1 : thumbs.length - 1;
      }
      thumbs[next].click();
    }, { passive: true });
  }
};

/* ============================================================
   Initialise Everything
   ============================================================ */

document.addEventListener('DOMContentLoaded', () => {
  CE.Drawer.init();
  CE.StickyHeader.init();
  CE.MegaMenu.init();
  CE.MobileNav.init();
  CE.Search.init();
  CE.Accordion.init();
  CE.QuantitySelector.init();
  CE.Gallery.init();

  // Cart updated → refresh count + drawer if open
  document.addEventListener('cart:updated', async () => {
    try {
      const cart = await CE.Cart.get();
      CE.Cart.updateCount(cart.item_count);
      const drawer = document.getElementById('CartDrawer');
      if (drawer && drawer.classList.contains('is-active')) {
        await CE.Cart.refreshDrawer();
      }
    } catch (e) {
      console.error('Cart update handler failed:', e);
    }
  });

  // Cart icon → open drawer
  // document.querySelectorAll('[data-cart-toggle]').forEach(btn => {
  //   btn.addEventListener('click', (e) => {
  //     e.preventDefault();
  //     CE.Drawer.open('CartDrawer');
  //     CE.Track.event('view_cart');
  //   });
  // });

  // Generic drawer toggles
  // document.querySelectorAll('[data-drawer-toggle]').forEach(btn => {
  //   btn.addEventListener('click', (e) => {
  //     e.preventDefault();
  //     CE.Drawer.toggle(btn.dataset.drawerToggle);
  //   });
  // });

  // // Generic drawer close buttons
  // document.querySelectorAll('[data-drawer-close]').forEach(btn => {
  //   btn.addEventListener('click', (e) => {
  //     e.preventDefault();
  //     CE.Drawer.closeAll();
  //   });
  // });

  // Load cart count on page load
  CE.Cart.get().then(cart => CE.Cart.updateCount(cart.item_count)).catch(() => {});
});

/* ============================================================
   BELVIA EDITORIAL POLISH — Session 8 (2026-05-07)
   Scroll-reveal observer, header scroll-condense, sticky ATC.
   ============================================================ */
(function belviaEditorial() {
  'use strict';

  // 1. Scroll-reveal — apply [data-reveal] to any section to fade-up on enter
  if ('IntersectionObserver' in window) {
    var revealObserver = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        if (entry.isIntersecting) {
          entry.target.classList.add('is-visible');
          revealObserver.unobserve(entry.target);
        }
      });
    }, { rootMargin: '0px 0px -10% 0px', threshold: 0.05 });

    document.querySelectorAll('[data-reveal]').forEach(function (el) {
      revealObserver.observe(el);
    });
  } else {
    document.querySelectorAll('[data-reveal]').forEach(function (el) {
      el.classList.add('is-visible');
    });
  }

  // 2. Header scroll-condense — shrinks at 80px, hides on scroll-down past 200px
  var header = document.querySelector('[data-header-wrapper]');
  if (header) {
    var lastScrollY = 0;
    var ticking = false;

    function updateHeader() {
      var y = window.pageYOffset;
      if (y > 80) header.classList.add('is-condensed'); else header.classList.remove('is-condensed');
      if (y > 200 && y > lastScrollY + 4) header.classList.add('is-hidden-scroll');
      else if (y < lastScrollY - 4 || y < 80) header.classList.remove('is-hidden-scroll');
      lastScrollY = y;
      ticking = false;
    }

    window.addEventListener('scroll', function () {
      if (!ticking) {
        window.requestAnimationFrame(updateHeader);
        ticking = true;
      }
    }, { passive: true });
  }

  // 3. Sticky ATC bar — appears when PDP gallery scrolls out of view
  var stickyAtc = document.querySelector('[data-sticky-atc]');
  var pdpGallery = document.querySelector('.pdp-gallery, .product-main__media-wrap, [data-pdp-gallery]');
  if (stickyAtc && pdpGallery && 'IntersectionObserver' in window) {
    var atcObserver = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        if (entry.isIntersecting) stickyAtc.classList.remove('is-visible');
        else stickyAtc.classList.add('is-visible');
      });
    }, { rootMargin: '-100px 0px 0px 0px' });
    atcObserver.observe(pdpGallery);
  }
})();
